// external
import type { FC, FocusEvent } from 'react';
import { useMemo } from 'react';

// interstate components
import { Box } from '@interstate/components/Box';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { ComboBoxOptions, ComboBoxOption } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import { selectFocusedInput, useSelectOnFocusEnabledStrict } from '@makemydeal/dr-shared-components/select-on-focus';
import { Tooltip } from '@interstate/components/Tooltip';
import { Typography } from '@interstate/components/Typography';
import { NumericInputContainer, FieldsContainer, SectionTitle } from './MileageCostOptions.style';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

// types
import type { MileageFormSectionProps, MileageFormField } from './mileageCostOptionsTypes';
import { useSelector } from 'react-redux';
import { buildComboOption } from '../terms/common/utils';
import { FIELD_TYPE } from './constants';
import { TextInput } from '@interstate/components/TextInput';
import FieldOverrideIndicator from '../terms/common/FieldOverrideIndicator.interstate';

// selectors
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

export type ComboBoxPairs = {
    id: number;
    name: string;
};

export const MileageFormSection: FC<MileageFormSectionProps> = ({ title, fields }) => {
    // Move hooks to component level
    const dataOptions = useSelector(offerReduxSelectors.getAnnualMilesOptions);

    const createOptions = useMemo((): ComboBoxOption[] => {
        return (dataOptions as ComboBoxPairs[])?.map((item: ComboBoxPairs) => buildComboOption(item)) || [];
    }, [JSON.stringify(dataOptions)]);

    const isAutoSelectEnabled = useSelectOnFocusEnabledStrict(
        featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
    );
    const handleComboBoxFocus = (e: FocusEvent<HTMLElement>) => {
        if (isAutoSelectEnabled) selectFocusedInput(e);
    };

    const renderField = (field: MileageFormField) => {
        const commonProps = {
            key: field.key,
            id: `${field.key}-input`,
            name: `${field.key}-input`,
            value: field.value ?? '',
            hasError: !!field.error,
            type: field.type,
            allowDecimal: field.allowDecimal,
            errorMessage: field.error,
            disabled: field.disabled,
            readOnly: field.readOnly,
            'data-testid': `${field.key}-input`
        };

        const handleOnChange = (e: InterstateOnChangeEvent<string | ComboBoxOptions>) => {
            let value: string;
            if (Array.isArray(e.target.value)) {
                value = e.target.value[0]?.value || '';
            } else {
                value = String(e.target.value);
            }
            field.onChange?.(field.key, value);
        };

        const getComboBoxValue = (): ComboBoxOptions | undefined => {
            if (!field.value) return undefined;
            const valueStr = String(field.value);
            return [{ label: valueStr, value: valueStr }];
        };

        const comboxBox = (
            <div style={{ display: 'contents' }} onFocus={handleComboBoxFocus}>
                <ComboBox
                    {...commonProps}
                    value={getComboBoxValue()}
                    options={createOptions}
                    onChange={handleOnChange}
                    allowNewEntry={true}
                    displayNoDataMessage={false}
                />
            </div>
        );
        const numbericInput = (
            <NumericInput
                {...commonProps}
                inputPrefix={field.type === FIELD_TYPE.CURRENCY ? '$' : undefined}
                textAlign="left"
                onChange={handleOnChange}
                decimalMaxLength={5}
                decimalMinLength={0}
                autoInsertCommas={true}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                strictSelectOnFocus
            />
        );
        const textInput = <TextInput {...commonProps} displayLabel={false} value={field.value as string} />;
        const formFields = {
            [FIELD_TYPE.DROPDOWN]: comboxBox,
            [FIELD_TYPE.NUMERIC]: numbericInput,
            [FIELD_TYPE.CURRENCY]: numbericInput,
            [FIELD_TYPE.TEXT]: textInput
        };
        const fieldContent = (
            <NumericInputContainer key={field.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography tag="span" variant="label-md" sx={({ tokens }) => ({ color: tokens.ComponentTextInputLabelColor })}>
                        {field.label}
                    </Typography>
                    {field.tooltip && (
                        <Tooltip size="large" position="right" toolTipContent={field.tooltip.content}>
                            <>{field.tooltip.icon}</>
                        </Tooltip>
                    )}
                </Box>
                {formFields[field.type]}
            </NumericInputContainer>
        );

        // These fields sit in a fixed grid, so the indicator must not reserve its border and icon slot.
        if (field.overrideIndicator) {
            return (
                <FieldOverrideIndicator key={field.key} {...field.overrideIndicator} reserveSpace={false} showTooltip={false}>
                    {fieldContent}
                </FieldOverrideIndicator>
            );
        }

        return fieldContent;
    };
    return (
        <Box sx={{ marginBottom: '2rem' }}>
            <SectionTitle variant="h4" color="primary">
                {title}
            </SectionTitle>

            <FieldsContainer>{fields.map(renderField)}</FieldsContainer>
        </Box>
    );
};
